import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Popover,
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  Divider,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  CalendarToday,
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';

interface CustomDateTimePickerProps {
  label: string;
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  required?: boolean;
  fullWidth?: boolean;
}

const CustomDateTimePicker: React.FC<CustomDateTimePickerProps> = ({
  label,
  value,
  onChange,
  required = false,
  fullWidth = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedHour, setSelectedHour] = useState(value?.hour() || 9);
  const [selectedMinute, setSelectedMinute] = useState(value?.minute() || 0);
  const textFieldRef = useRef<HTMLInputElement>(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (value) {
      setCurrentMonth(value);
      setSelectedHour(value.hour());
      setSelectedMinute(value.minute());
    }
  }, [value]);

  const handleClick = () => {
    setAnchorEl(textFieldRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDateSelect = (date: Dayjs) => {
    const newDateTime = date.hour(selectedHour).minute(selectedMinute);
    onChange(newDateTime);
  };

  const handleTimeChange = (hour: number, minute: number) => {
    setSelectedHour(hour);
    setSelectedMinute(minute);
    if (value) {
      const newDateTime = value.hour(hour).minute(minute);
      onChange(newDateTime);
    }
  };

  const handleOk = () => {
    if (value) {
      const newDateTime = value.hour(selectedHour).minute(selectedMinute);
      onChange(newDateTime);
    }
    handleClose();
  };

  const formatDisplayValue = (date: Dayjs | null) => {
    if (!date) return '';
    return date.format('DD/MM/YYYY HH:mm');
  };

  const getDaysInMonth = () => {
    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');
    const startDate = startOfMonth.startOf('week');
    const endDate = endOfMonth.endOf('week');
    
    const days = [];
    let current = startDate;
    
    while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
      days.push(current);
      current = current.add(1, 'day');
    }
    
    return days;
  };

  const isToday = (date: Dayjs) => {
    return date.isSame(dayjs(), 'day');
  };

  const isSelected = (date: Dayjs) => {
    return value && date.isSame(value, 'day');
  };

  const isCurrentMonth = (date: Dayjs) => {
    return date.isSame(currentMonth, 'month');
  };

  const days = getDaysInMonth();
  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <>
      <TextField
        ref={textFieldRef}
        label={label}
        value={formatDisplayValue(value)}
        onClick={handleClick}
        fullWidth={fullWidth}
        required={required}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <IconButton onClick={handleClick} edge="end">
              <CalendarToday />
            </IconButton>
          ),
        }}
        sx={{ cursor: 'pointer' }}
      />
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Paper sx={{ p: 2, width: 420 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
            {/* Calendar Section */}
            <Box sx={{ flex: 1 }}>
              {/* Month Navigation */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <IconButton 
                  onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))}
                  size="small"
                >
                  <ChevronLeft />
                </IconButton>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {currentMonth.format('MMMM YYYY')}
                </Typography>
                <IconButton 
                  onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))}
                  size="small"
                >
                  <ChevronRight />
                </IconButton>
              </Box>

              {/* Week Days Header */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, mb: 1 }}>
                {weekDays.map((day) => (
                  <Box key={day} sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      {day}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Calendar Days */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0 }}>
                {days.map((day, index) => (
                  <Box key={index} sx={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={() => handleDateSelect(day)}
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: '0.875rem',
                        backgroundColor: isSelected(day) ? 'primary.main' : 'transparent',
                        color: isSelected(day) ? 'white' : isCurrentMonth(day) ? 'text.primary' : 'text.disabled',
                        border: isToday(day) && !isSelected(day) ? '2px solid' : 'none',
                        borderColor: isToday(day) && !isSelected(day) ? 'primary.main' : 'transparent',
                        '&:hover': {
                          backgroundColor: isSelected(day) ? 'primary.dark' : 'action.hover',
                        },
                      }}
                    >
                      {day.date()}
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Time Section */}
            <Box sx={{ width: 140 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Time
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Hour Selection */}
                <Box sx={{ width: 65 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                    Hour
                  </Typography>
                  <Box sx={{ 
                    height: 200, 
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '3px',
                    },
                  }}>
                    {Array.from({ length: 24 }, (_, i) => (
                      <Button
                        key={i}
                        onClick={() => handleTimeChange(i, selectedMinute)}
                        fullWidth
                        size="small"
                        sx={{
                          justifyContent: 'center',
                          minHeight: 32,
                          backgroundColor: selectedHour === i ? 'primary.main' : 'transparent',
                          color: selectedHour === i ? 'white' : 'text.primary',
                          '&:hover': {
                            backgroundColor: selectedHour === i ? 'primary.dark' : 'action.hover',
                          },
                          borderRadius: 0,
                          minWidth: 0,
                          padding: '6px 8px',
                        }}
                      >
                        {i.toString().padStart(2, '0')}
                      </Button>
                    ))}
                  </Box>
                </Box>

                {/* Minute Selection */}
                <Box sx={{ width: 65 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                    Minute
                  </Typography>
                  <Box sx={{ 
                    height: 200, 
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '3px',
                    },
                  }}>
                    {Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => (
                      <Button
                        key={minute}
                        onClick={() => handleTimeChange(selectedHour, minute)}
                        fullWidth
                        size="small"
                        sx={{
                          justifyContent: 'center',
                          minHeight: 32,
                          backgroundColor: selectedMinute === minute ? 'primary.main' : 'transparent',
                          color: selectedMinute === minute ? 'white' : 'text.primary',
                          '&:hover': {
                            backgroundColor: selectedMinute === minute ? 'primary.dark' : 'action.hover',
                          },
                          borderRadius: 0,
                          minWidth: 0,
                          padding: '6px 8px',
                        }}
                      >
                        {minute.toString().padStart(2, '0')}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button onClick={handleClose} size="small">
              Cancel
            </Button>
            <Button onClick={handleOk} variant="contained" size="small">
              OK
            </Button>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default CustomDateTimePicker;
